import { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Route, Switch } from 'react-router-dom';

import { useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { AppLayout, NoPrintWrapper, Popover } from '@treams/treams-ui';

import { RecordingBanner } from '../Recording/RecordingBanner';
import Billing from '../billing/Billing';
import Banner from '../layout/components/Banner';
import AppNav from '../navigation/AppNav';
import ManagerModeProvider from '../navigation/ManagerModeContext';
import TopBar from '../navigation/TopBar';
import BillingSuccess from '../onboarding/BillingSuccess';
import TrialExpired from '../onboarding/TrialExpired';

export default function Layout(props: PropsWithChildren) {
	const workspace = useWorkspace();

	const query = graphql.useViewerWorkspacePlanQuery();
	const roles = useViewerRoles();

	const [navOpen, setNavOpen] = useState(false);
	const mainRef = useRef<HTMLElement>(null);

	const location = useLocation<{ preventScrollReset?: boolean }>();

	useEffect(() => setNavOpen(false), [location.key]);

	useLayoutEffect(() => {
		if (location.state?.preventScrollReset) {
			return;
		}

		mainRef.current?.scrollTo(0, 0);
	}, [location.pathname, location.state]);

	const isExpired = query.data?.viewer.workspace.isExpired ?? false;

	if (workspace.id === 'launch') {
		return (
			<AppLayout
				navOpen={navOpen}
				nav={<AppNav onClose={!navOpen ? undefined : () => setNavOpen(false)} />}
				header={<TopBar />}
				mainRef={mainRef}
			>
				<NoPrintWrapper>
					<RecordingBanner />
				</NoPrintWrapper>
				{props.children}
			</AppLayout>
		);
	}

	return (
		<Switch>
			<Route path="/settings/billing/success">
				<BillingSuccess />
			</Route>
			<Route>
				<ManagerModeProvider>
					<AppLayout
						banner={<Banner />}
						navOpen={navOpen}
						nav={<AppNav onClose={!navOpen ? undefined : () => setNavOpen(false)} />}
						header={<TopBar onOpen={navOpen ? undefined : () => setNavOpen(true)} />}
						mainRef={mainRef}
					>
						<NoPrintWrapper>
							<RecordingBanner />
						</NoPrintWrapper>
						{props.children}
					</AppLayout>
				</ManagerModeProvider>

				{isExpired && !roles.loading && (
					<Popover center visible>
						{roles.isWorkspaceSuperuser ? <Billing modal /> : <TrialExpired employee modal />}
					</Popover>
				)}
			</Route>
		</Switch>
	);
}
